/**
 * NotificationPreview — Mockup CSS puro de la notificación push (§8.3.2).
 *
 * Tabs iOS | Android + toggle Colapsada | Expandida. Trunca título/descr según
 * plataforma (iOS ~50/150, Android 65/240), muestra imagen 16:9 SOLO en
 * Android + Expandida y botones de acción solo en Expandida.
 * Se actualiza con debounce de 300 ms.
 */
import { useEffect, useState } from 'react';
import { CATALOGO_BOTONES, TRUNCADO_PREVIEW, type BotonId } from './tiposCampana';
import styles from './NotificationPreview.module.css';

export interface PreviewContenido {
  titulo: string;
  descripcion: string;
  imagenUrl: string | null;
  botones: BotonId[];
}

interface NotificationPreviewProps {
  contenido: PreviewContenido;
}

type Plataforma = 'ios' | 'android';

function truncar(texto: string, max: number): string {
  if (texto.length <= max) return texto;
  return `${texto.slice(0, max)}…`;
}

/** Debounce interno de 300 ms para no re-renderizar el mockup en cada tecla. */
function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export function NotificationPreview({ contenido }: NotificationPreviewProps) {
  const [plataforma, setPlataforma] = useState<Plataforma>('android');
  const [expandida, setExpandida] = useState(true);
  const c = useDebounced(contenido, 300);

  const limites = TRUNCADO_PREVIEW[plataforma];
  const titulo = truncar(c.titulo || 'Título de la notificación', limites.titulo);
  const descripcion = truncar(
    c.descripcion || 'El texto de la descripción aparecerá aquí.',
    limites.descripcion,
  );

  const mostrarImagen = plataforma === 'android' && expandida && !!c.imagenUrl;
  const mostrarBotones = expandida && c.botones.length > 0;

  return (
    <div className={styles.wrapper}>
      <div className={styles.controls}>
        <div className={styles.tabs} role="tablist" aria-label="Plataforma del preview">
          <button
            type="button"
            role="tab"
            aria-selected={plataforma === 'ios'}
            className={`${styles.tab} ${plataforma === 'ios' ? styles.tabActive : ''}`}
            onClick={() => setPlataforma('ios')}
          >
            iOS
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={plataforma === 'android'}
            className={`${styles.tab} ${plataforma === 'android' ? styles.tabActive : ''}`}
            onClick={() => setPlataforma('android')}
          >
            Android
          </button>
        </div>
        <div className={styles.tabs} role="tablist" aria-label="Estado de la notificación">
          <button
            type="button"
            role="tab"
            aria-selected={!expandida}
            className={`${styles.tab} ${!expandida ? styles.tabActive : ''}`}
            onClick={() => setExpandida(false)}
          >
            Colapsada
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={expandida}
            className={`${styles.tab} ${expandida ? styles.tabActive : ''}`}
            onClick={() => setExpandida(true)}
          >
            Expandida
          </button>
        </div>
      </div>

      <div className={styles.screen} aria-label={`Vista previa en ${plataforma === 'ios' ? 'iPhone' : 'Android'}`}>
        <div className={styles.hora} aria-hidden="true">
          {new Intl.DateTimeFormat('es-PA', {
            timeZone: 'America/Panama',
            hour: 'numeric',
            minute: '2-digit',
          }).format(new Date())}
        </div>

        <div className={`${styles.notif} ${plataforma === 'ios' ? styles.notifIos : ''}`}>
          <div className={styles.notifHeader}>
            <span className={styles.appIcon} aria-hidden="true">M</span>
            <span className={styles.appName}>MinSalud</span>
            <span className={styles.dot} aria-hidden="true">·</span>
            <span className={styles.timeAgo}>ahora</span>
          </div>

          <div className={styles.notifBody}>
            <p className={styles.notifTitle}>{titulo}</p>
            <p className={styles.notifText}>{descripcion}</p>
          </div>

          {mostrarImagen && c.imagenUrl && (
            <div className={styles.notifImageWrap}>
              <img src={c.imagenUrl} alt="Imagen de la campaña" className={styles.notifImage} />
            </div>
          )}

          {mostrarBotones && (
            <div className={styles.notifActions}>
              {c.botones.map((id) => (
                <span key={id} className={styles.notifAction}>
                  {CATALOGO_BOTONES.find((b) => b.id === id)?.label ?? id}
                </span>
              ))}
            </div>
          )}
        </div>

        {plataforma === 'ios' && c.imagenUrl && (
          <p className={styles.iosNote}>
            En esta fase, iPhone muestra la notificación sin imagen.
          </p>
        )}
      </div>
    </div>
  );
}
